import React, { useMemo } from "react";
import { ArrowDown, Paperclip, Check } from "lucide-react";
import { Button } from "@shared/ui/button";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { InventoryResource, ActiveResource, ModalMode } from "./AddResourceModal";

interface ResourceSummaryStepProps {
  mode: ModalMode;
  newResource: InventoryResource | null;
  replacingResource: ActiveResource | null;
  onBack: () => void;
  onConfirm: () => void;
}

const getServiceDotColor = (service: string) => {
  switch (service) {
    case "Water":
      return "bg-blue-500";
    case "Electricity":
      return "bg-amber-500";
    case "Gas":
      return "bg-orange-500";
    case "Solar":
      return "bg-emerald-500";
    case "Hot Water":
      return "bg-red-500";
    case "Waste Water":
      return "bg-cyan-500";
    case "Broadband":
      return "bg-purple-500";
    case "EV":
      return "bg-emerald-500";
    default:
      return "bg-gray-400";
  }
};

const getServiceCardStyle = (service: string) => {
  switch (service) {
    case "Water":
      return "bg-blue-50/40 border-blue-200 text-blue-900";
    case "Electricity":
      return "bg-amber-50/40 border-amber-200 text-amber-900";
    case "Gas":
      return "bg-orange-50/40 border-orange-200 text-orange-900";
    case "Solar":
      return "bg-emerald-50/40 border-emerald-200 text-emerald-900";
    case "Hot Water":
      return "bg-red-50/40 border-red-200 text-red-900";
    case "Waste Water":
      return "bg-cyan-50/40 border-cyan-200 text-cyan-900";
    case "Broadband":
      return "bg-purple-50/40 border-purple-200 text-purple-900";
    case "EV":
      return "bg-emerald-50/40 border-emerald-200 text-emerald-900";
    default:
      return "bg-gray-50/40 border-gray-200 text-gray-900";
  }
};

const getServiceIconBg = (service: string) => {
  switch (service) {
    case "Water":
      return "bg-blue-100 border-blue-200";
    case "Electricity":
      return "bg-amber-100 border-amber-200";
    case "Gas":
      return "bg-orange-100 border-orange-200";
    case "Solar":
      return "bg-emerald-100 border-emerald-200";
    default:
      return "bg-gray-100 border-gray-200";
  }
};

export const ResourceSummaryStep: React.FC<ResourceSummaryStepProps> = ({
  mode,
  newResource,
  replacingResource,
  onBack,
  onConfirm,
}) => {
  const selectedResourceDetails = useMemo(() => {
    if (!newResource) return [];
    return [
      { label: "Utility Service", value: newResource.service },
      { label: "Resource Type", value: newResource.type },
      { label: "Sub Type", value: newResource.subType },
      { label: "Manufacturer", value: newResource.manufacturer },
      { label: "Model", value: newResource.model },
      { label: "Capacity", value: newResource.capacity },
      { label: "Location", value: newResource.location },
    ];
  }, [newResource]);

  const updateResourceDetails = useMemo(() => {
    if (!newResource) return [];
    return [
      { label: "Service", value: newResource.service },
      { label: "Type", value: newResource.type },
      { label: "Sub Type", value: newResource.subType },
      { label: "Manufacturer", value: newResource.manufacturer },
      { label: "Model", value: newResource.model },
      { label: "Capacity", value: newResource.capacity },
    ];
  }, [newResource]);

  if (!newResource) return null;

  // Add New Summary Screen
  if (mode === "add_new") {
    return (
      <div className="py-2 space-y-5">
        <span className="block text-xs font-semibold text-gray-600">
          Resource Summary
        </span>

        {/* Color-Coded Resource Card */}
        <div
          className={`p-5 rounded-xl border space-y-4 shadow-2xs ${getServiceCardStyle(
            newResource.service,
          )}`}
        >
          {/* Header Row: Icon, Resource ID, Serial, Available badge */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3.5">
              <div
                className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${getServiceIconBg(
                  newResource.service,
                )}`}
              >
                <span
                  className={`w-3 h-3 rounded-full ${getServiceDotColor(
                    newResource.service,
                  )}`}
                />
              </div>
              <div className="space-y-0.5">
                <h3 className="text-base font-bold text-blue-600">
                  {newResource.resourceId}
                </h3>
                <p className="text-xs text-gray-500 font-mono">
                  {newResource.serialNo}
                </p>
              </div>
            </div>

            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/80 inline-flex items-center gap-1.5 shadow-2xs">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Available
            </span>
          </div>

          {/* Details Grid using ValueLabel */}
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200/70">
            {selectedResourceDetails.map((detail, index) => (
              <ValueLabel
                key={index}
                label={detail.label}
                value={detail.value}
              />
            ))}
          </div>
        </div>

        {/* Form Action Buttons */}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            type="button"
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5"
            onClick={onConfirm}
          >
            <Paperclip className="w-3.5 h-3.5" />
            Attach Resource
          </Button>
        </div>
      </div>
    );
  }

  // Update Existing Summary Screen
  return (
    <div className="py-2 space-y-4">
      {/* 1. Current (Removing) Section */}
      <div className="space-y-1">
        <span className="block text-xs font-semibold text-gray-600">
          Current (Removing)
        </span>
        {replacingResource && (
          <div className="p-3 bg-gray-100/80 border border-gray-200 rounded-xl flex items-center justify-between opacity-80">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg border bg-gray-200 border-gray-300 flex items-center justify-center shrink-0">
                <span
                  className={`w-2 h-2 rounded-full ${getServiceDotColor(
                    replacingResource.service,
                  )}`}
                />
              </div>
              <div className="space-y-0.5">
                <h5 className="text-xs font-bold text-gray-800 line-through decoration-red-500">
                  {replacingResource.meterId}
                </h5>
                <p className="text-[11px] text-gray-500">
                  {replacingResource.service} · {replacingResource.type} ·{" "}
                  {replacingResource.serialNo}
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-semibold text-gray-500">
                Last: {replacingResource.lastReading}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Downward Arrow */}
      <div className="flex justify-center -my-1">
        <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-200 text-blue-600 flex items-center justify-center shadow-2xs">
          <ArrowDown className="w-4 h-4" />
        </div>
      </div>

      {/* 2. New Resource Section */}
      <div className="space-y-1">
        <span className="block text-xs font-semibold text-gray-600">
          New Resource
        </span>
        <div
          className={`p-4 rounded-xl border space-y-3 shadow-2xs ${getServiceCardStyle(
            newResource.service,
          )}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${getServiceIconBg(
                  newResource.service,
                )}`}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full ${getServiceDotColor(
                    newResource.service,
                  )}`}
                />
              </div>
              <div className="space-y-0.5">
                <h4 className="text-sm font-bold text-blue-600">
                  {newResource.resourceId}
                </h4>
                <p className="text-[11px] text-gray-500 font-mono">
                  {newResource.serialNo}
                </p>
              </div>
            </div>

            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/80 inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Available
            </span>
          </div>

          {/* Details Grid using ValueLabel */}
          <div className="grid grid-cols-3 gap-4 pt-2 border-t border-gray-200/70">
            {updateResourceDetails.map((detail, index) => (
              <ValueLabel
                key={index}
                label={detail.label}
                value={detail.value}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Form Action Buttons */}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5"
          onClick={onConfirm}
        >
          <Check className="w-4 h-4" />
          Confirm Update
        </Button>
      </div>
    </div>
  );
};
